import { DialogRef } from '@angular/cdk/dialog';
import { Component, computed, inject, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  EmpoweredProductLicensesStore,
  EmpoweredProductLicensesStoreType,
  PartnersStore,
  PartnersStoreType,
  ProductsStore,
  ProductsStoreType,
} from '@appState';
import { ADD_DIALOG_NO, ADD_DIALOG_YES } from '@models/constants';
import {
  Choice,
  configureCloseEffectForDialog,
  CuiButtonComponent,
  CuiButtonCtaComponent,
  CuiCardModule,
  CuiFormComponent,
  CuiPreventDialogCloseDirective,
  CuiSearchSelectComponent,
  CuiSearchSelectSsrComponent,
  CuiSimpleSelectComponent,
  CuiValidators,
  deepEqual,
  SearchSelectItem,
  SubscribingBase,
} from 'cui-components';
import { AppConfigService } from 'src/app/infrastructure';

type AddRequestForm = {
  corePartnerId: FormControl<string>;
  coreCustomerId: FormControl<string>;
  productId: FormControl<string>;
};

@Component({
  selector: 'hui-add-request-dialog',
  imports: [
    ReactiveFormsModule,
    CuiCardModule,
    CuiButtonComponent,
    CuiButtonCtaComponent,
    CuiFormComponent,
    CuiSimpleSelectComponent,
    CuiSearchSelectSsrComponent,
    CuiPreventDialogCloseDirective,
    CuiSearchSelectComponent,
  ],
  template: `
    <cui-card [isLoading]="isLoading()" cuiPreventDialogClose>
      <cui-card-header [showCloseBtn]="true" (close)="onCancelClick()">
        <h3>Add new request</h3>
      </cui-card-header>
      <cui-card-body>
        <cui-form [formGroup]="form" (save)="onAddClick()">
          <cui-search-select-ssr formControlName="corePartnerId" placeholder="Partner" [entity]="'Partners'"></cui-search-select-ssr>
          <cui-search-select formControlName="coreCustomerId" [placeholder]="'Customer'" [items]="customerChoices()"></cui-search-select>
          <cui-simple-select formControlName="productId" [label]="'Product'" [choices]="productChoices()"></cui-simple-select>
        </cui-form>
      </cui-card-body>
      <cui-card-footer>
        <cui-button (clicked)="onCancelClick()">{{ ADD_DIALOG_NO }}</cui-button>
        <cui-button-cta [disabled]="form.disabled || !form.valid || !form.dirty || isLoading()" (clicked)="cuiForm().submit()">{{
          ADD_DIALOG_YES
        }}</cui-button-cta>
      </cui-card-footer>
    </cui-card>
  `,
  styles: `
    cui-search-select-ssr,
    cui-search-select {
      background-color: var(--cui-color-card-bg);
    }
  `,
})
export class AddRequestDialogComponent extends SubscribingBase {
  private empoweredProductLicensesStore: EmpoweredProductLicensesStoreType = inject(EmpoweredProductLicensesStore);
  private productsStore: ProductsStoreType = inject(ProductsStore);
  private partnersStore: PartnersStoreType = inject(PartnersStore);
  private dialogRef: DialogRef = inject(DialogRef);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private appConfig: AppConfigService = inject(AppConfigService);

  cuiForm: Signal<CuiFormComponent> = viewChild.required(CuiFormComponent);
  isLoading: Signal<boolean> = computed<boolean>(
    () => this.empoweredProductLicensesStore.isCreateLoading() || this.productsStore.isLoading(),
  );

  protected get availableProductIds(): string[] {
    return [
      this.appConfig.config['FRONTEND_IRISH_LOCALISATION_PRODUCTID']!,
      this.appConfig.config['FRONTEND_POLISH_LOCALISATION_PRODUCTID']!,
      this.appConfig.config['FRONTEND_PORTUGESE_LOCALISATION_PRODUCTID']!,
      this.appConfig.config['FRONTEND_BRAZILIAN_LOCALISATION_PRODUCTID']!,
      this.appConfig.config['FRONTEND_EMPOWERED_PRODUCTID']!,
    ];
  }

  productChoices: Signal<Choice[]> = computed<Choice[]>(
    () =>
      this.productsStore
        .products()
        .filter(p => this.availableProductIds.includes(p.id))
        .map(p => ({ value: p.id, text: p.name })),
    { equal: deepEqual },
  );

  currentPartnerId: WritableSignal<string | null> = signal(null);

  customerChoices: Signal<SearchSelectItem[]> = computed(() => {
    const partnerId = this.currentPartnerId();
    if (!partnerId) return [];
    const customers = this.partnersStore.partnerCustomers();
    const ownCustomer = customers.find(c => c.ownPartner);
    const choices: SearchSelectItem[] = [];
    if (ownCustomer) {
      choices.push({ value: ownCustomer.customerId, name: `${ownCustomer.name} (own partner)` });
    }

    choices.push(
      ...customers
        .filter(c => c.customerId !== ownCustomer?.customerId)
        .map(c => ({ value: c.customerId, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    );

    return choices;
  });

  form: FormGroup<AddRequestForm> = this.formBuilder.group<AddRequestForm>({
    corePartnerId: new FormControl('', { nonNullable: true, validators: [CuiValidators.required] }),
    coreCustomerId: new FormControl('', { nonNullable: true, validators: [CuiValidators.required] }),
    productId: new FormControl('', { nonNullable: true, validators: [CuiValidators.required] }),
  });

  readonly ADD_DIALOG_YES = ADD_DIALOG_YES;
  readonly ADD_DIALOG_NO = ADD_DIALOG_NO;

  constructor() {
    super();

    if (!this.productsStore.products().length && !this.productsStore.isLoading()) {
      this.productsStore.getAllProducts();
    }

    this.subscribe(this.form.controls.corePartnerId.valueChanges, (partnerId: string) => {
      this.currentPartnerId.set(partnerId || null);
      this.form.controls.coreCustomerId.reset('');
      if (partnerId) {
        this.partnersStore.getPartnerCustomers(partnerId);
      }
    });

    configureCloseEffectForDialog(this.dialogRef, this.empoweredProductLicensesStore.isCreateLoading);
  }

  onAddClick(): void {
    if (this.form.valid) {
      this.empoweredProductLicensesStore.create(this.form.getRawValue());
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
